import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  toasts: Toast[];
  toast: (toast: Omit<Toast, 'id'>) => void;
  success: (message: string, title?: string, duration?: number) => void;
  error: (message: string, title?: string, duration?: number) => void;
  warning: (message: string, title?: string, duration?: number) => void;
  info: (message: string, title?: string, duration?: number) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const toast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const duration = toast.duration ?? 5000;

    const newToast: Toast = {
      ...toast,
      id,
      duration,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        dismiss(id);
      }, duration);
    }
  }, [dismiss]);

  const success = useCallback((message: string, title?: string, duration?: number) => {
    toast({ type: 'success', message, title, duration });
  }, [toast]);

  const error = useCallback((message: string, title?: string, duration?: number) => {
    toast({ type: 'error', message, title, duration: duration ?? 7000 }); // Longer duration for errors
  }, [toast]);

  const warning = useCallback((message: string, title?: string, duration?: number) => {
    toast({ type: 'warning', message, title, duration: duration ?? 6000 });
  }, [toast]);

  const info = useCallback((message: string, title?: string, duration?: number) => {
    toast({ type: 'info', message, title, duration });
  }, [toast]);

  return (
    <ToastContext.Provider value={{ toasts, toast, success, error, warning, info, dismiss, dismissAll }}>
      {children}
      {toasts.length > 0 && createPortal(
        <ToastContainer toasts={toasts} onDismiss={dismiss} />,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Toast Container Component
interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-md pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

// Individual Toast Component
interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-accent-green flex-shrink-0" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-accent-red flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-accent-orange flex-shrink-0" />;
      case 'info':
        return <Info className="w-5 h-5 text-accent-blue flex-shrink-0" />;
    }
  };

  const getBgColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-accent-green-faded dark:bg-accent-green-faded border-accent-green';
      case 'error':
        return 'bg-accent-red-faded dark:bg-accent-red-faded border-accent-red';
      case 'warning':
        return 'bg-accent-orange-faded dark:bg-accent-orange-faded border-accent-orange';
      case 'info':
        return 'bg-accent-blue-faded dark:bg-accent-blue-faded border-accent-blue';
    }
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 rounded border shadow-lg pointer-events-auto',
        'bg-card',
        'backdrop-blur-sm',
        'animate-slide-in-right',
        getBgColor()
      )}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      {getIcon()}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <div className="font-medium text-sm text-foreground mb-1">
            {toast.title}
          </div>
        )}
        <div className="text-sm text-muted-foreground">
          {toast.message}
        </div>
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className="mt-2 text-sm font-medium text-accent-blue hover:text-accent-blue-hover transition-colors"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Dismiss Button */}
      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 text-muted-foreground/70 hover:text-foreground transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

